import React, { useState, useEffect, useCallback } from 'react';
import TopBar from './TopBar';
import AssistantSidebar from './AssistantSidebar';
import UtilitySidebar from './UtilitySidebar';
import SelectionOverlay from './SelectionOverlay';
import { API_BASE_URL } from '../../config/api';

/**
 * AppShell — master wrapper.
 *
 * The tools and tasks buttons no longer open their own modals. Instead
 * they toggle overlays inside the launcher's panel (same panel that
 * shows the assistant conversation). This unifies all three panel-
 * accessing buttons (Chats / Tools / Tasks) into one surface. The
 * launcher tracks `activeOverlay` and broadcasts it back to the pill.
 */
export default function AppShell({
  children,
  rightPanel = null,
  rightPanelWidth = null,
  pageStrip,
  leftPanel,
  variant = 'recruiter',
  onAddClick,
  onExport,
  onImport,
}) {
  const [assistantInput, setAssistantInput] = useState('');

  // Launcher-broadcast state.
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantWidth, setAssistantWidth] = useState(448);
  const [activeOverlay, setActiveOverlay] = useState(null); // 'history' | 'tools' | 'tasks' | null
  const [currentTitle, setCurrentTitle] = useState(null);

  // Task count — fetched from the server. Refreshes on mount and
  // whenever `banyak:tasks_updated` fires (TasksContent broadcasts
  // that after create/complete/delete).
  //
  // Errors are logged loudly with a [banyak-tasks] prefix. If the badge
  // isn't showing when you expect it to, open dev tools and look for
  // that prefix — you'll see the fetch URL, response status, and body.
  const [taskCount, setTaskCount] = useState(null);
  const refreshTaskCount = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.warn('[banyak-tasks] no authToken in localStorage — skipping fetch');
      return;
    }
    const url = `${API_BASE_URL}/api/tasks/count`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const body = await res.text().catch(() => '<no body>');
        console.error('[banyak-tasks] count fetch failed', {
          url, status: res.status, body: body.slice(0, 200),
        });
        return;
      }
      const data = await res.json();
      console.log('[banyak-tasks] count response', data);
      setTaskCount(typeof data.count === 'number' ? data.count : 0);
    } catch (e) {
      console.error('[banyak-tasks] count fetch threw', { url, error: e });
    }
  }, []);
  useEffect(() => {
    refreshTaskCount();
    const handler = () => refreshTaskCount();
    window.addEventListener('banyak:tasks_updated', handler);
    return () => window.removeEventListener('banyak:tasks_updated', handler);
  }, [refreshTaskCount]);

  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail || {};
      if (typeof d.open === 'boolean') setAssistantOpen(d.open);
      if (typeof d.width === 'number') setAssistantWidth(d.width);
      if ('activeOverlay' in d) setActiveOverlay(d.activeOverlay);
      if ('currentTitle' in d) setCurrentTitle(d.currentTitle);
    };
    window.addEventListener('banyak:assistant_state', handler);
    return () => window.removeEventListener('banyak:assistant_state', handler);
  }, []);

  const handleFirstKeystroke = useCallback(() => {
    window.dispatchEvent(new CustomEvent('banyak:assistant_open'));
  }, []);

  const handleAssistantSubmit = useCallback((text) => {
    window.dispatchEvent(
      new CustomEvent('banyak:assistant_open_with_text', { detail: { text } })
    );
    setAssistantInput('');
  }, []);

  const handleToggleHistory = useCallback(() => {
    window.dispatchEvent(new CustomEvent('banyak:assistant_toggle_history'));
  }, []);
  const handleToggleTools = useCallback(() => {
    window.dispatchEvent(new CustomEvent('banyak:assistant_toggle_tools'));
  }, []);
  const handleToggleTasks = useCallback(() => {
    window.dispatchEvent(new CustomEvent('banyak:assistant_toggle_tasks'));
  }, []);

  // Inline left assistant sidebar — open state persists across page
  // navigation (AppShell is mounted per-page, so without persistence the
  // panel would close every time you switch pages). Width is also saved.
  const [assistantSidebarOpen, setAssistantSidebarOpen] = useState(() => {
    try { return localStorage.getItem('banyak:assistant_sidebar_open') === '1'; } catch { return false; }
  });
  const [assistantSidebarWidth, setAssistantSidebarWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('banyak:assistant_sidebar_width') || '', 10);
    return Number.isFinite(saved) && saved >= 320 && saved <= 720 ? saved : 400;
  });
  const [resizingSidebar, setResizingSidebar] = useState(false);
  const toggleAssistantSidebar = useCallback(() => setAssistantSidebarOpen(o => !o), []);
  const closeAssistantSidebar = useCallback(() => setAssistantSidebarOpen(false), []);

  useEffect(() => {
    try { localStorage.setItem('banyak:assistant_sidebar_open', assistantSidebarOpen ? '1' : '0'); } catch {}
  }, [assistantSidebarOpen]);

  // Broadcast open state so the AssistantLauncher engine knows to suppress
  // its floating shell (and portal into the sidebar chat slot instead).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('banyak:assistant_sidebar_state', { detail: { open: assistantSidebarOpen } }));
  }, [assistantSidebarOpen]);

  useEffect(() => {
    if (!resizingSidebar) return;
    const onMove = (e) => {
      const w = Math.min(720, Math.max(320, e.clientX));
      setAssistantSidebarWidth(w);
    };
    const onUp = () => {
      setResizingSidebar(false);
      try { localStorage.setItem('banyak:assistant_sidebar_width', String(assistantSidebarWidth)); } catch {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizingSidebar, assistantSidebarWidth]);

  // Right-side utility sidebar — Tools & Tasks, opened from the nav icons.
  const [utilityView, setUtilityView] = useState(null); // null | 'tools' | 'tasks'
  const [utilityWidth, setUtilityWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('banyak:utility_sidebar_width') || '', 10);
    return Number.isFinite(saved) && saved >= 320 && saved <= 640 ? saved : 380;
  });
  const [resizingUtility, setResizingUtility] = useState(false);
  const handleUtilityClick = useCallback((view) => {
    setUtilityView(cur => (cur === view ? null : view));
  }, []);
  useEffect(() => {
    if (!resizingUtility) return;
    const onMove = (e) => {
      const w = Math.min(640, Math.max(320, window.innerWidth - e.clientX));
      setUtilityWidth(w);
    };
    const onUp = () => {
      setResizingUtility(false);
      try { localStorage.setItem('banyak:utility_sidebar_width', String(utilityWidth)); } catch {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizingUtility, utilityWidth]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-50 flex">
      {/* Inline assistant sidebar — full height at the far left, so it moves
          the entire app (top bar included) to the right rather than sitting
          under the bar. Amber-themed to read as a distinct assistant space. */}
      {assistantSidebarOpen && (
        <>
          <div
            className="flex-shrink-0 overflow-hidden flex flex-col"
            style={{ width: `${assistantSidebarWidth}px` }}
          >
            <AssistantSidebar
              width={assistantSidebarWidth}
              onClose={closeAssistantSidebar}
              onTaskCountChange={refreshTaskCount}
            />
          </div>
          <div
            onMouseDown={() => setResizingSidebar(true)}
            className={`flex-shrink-0 w-1 cursor-col-resize hover:bg-gray-400 transition-colors ${resizingSidebar ? 'bg-gray-400' : 'bg-gray-200'}`}
            title="Drag to resize"
          />
        </>
      )}

      {/* Main column — top bar, page strip, content */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <TopBar
          variant={variant}
          onAddClick={onAddClick}
          onExport={onExport}
          onImport={onImport}
          taskCount={taskCount}
          onAssistantClick={toggleAssistantSidebar}
          assistantOpen={assistantSidebarOpen}
          onUtilityClick={handleUtilityClick}
          utilityView={utilityView}
        />

        {pageStrip && (
          <div className="flex-shrink-0 bg-white border-b border-gray-200">
            {pageStrip}
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {leftPanel && (
            <div className="flex-shrink-0 w-[260px] bg-white border-r border-gray-200 overflow-y-auto">
              {leftPanel}
            </div>
          )}
          <main className="flex-1 overflow-hidden relative">
            {children}
          </main>

          {/* Generic page-supplied right panel (e.g. edit drawers). Sits
              BELOW the second-layer bar (pageStrip) and pushes content left —
              a flex sibling of <main>, full height of the content area. */}
          {rightPanel && (
            <div
              className="flex-shrink-0 overflow-hidden flex flex-col border-l border-gray-200 bg-white"
              style={{ width: `${rightPanelWidth || 448}px` }}
            >
              {rightPanel}
            </div>
          )}

          {/* Right-side utility sidebar — Tools & Tasks. */}
          {utilityView && (
            <>
              <div
                onMouseDown={() => setResizingUtility(true)}
                className={`flex-shrink-0 w-1 cursor-col-resize hover:bg-emerald-400 transition-colors ${resizingUtility ? 'bg-emerald-400' : 'bg-gray-200'}`}
                title="Drag to resize"
              />
              <div
                className="flex-shrink-0 overflow-hidden flex flex-col border-l border-gray-200"
                style={{ width: `${utilityWidth}px` }}
              >
                <UtilitySidebar
                  view={utilityView}
                  onSelectView={setUtilityView}
                  onClose={() => setUtilityView(null)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Point-and-click context selection layer (activated from the AI panel) */}
      <SelectionOverlay />
    </div>
  );
}
